import classNames from "classnames";
import { If } from "react-conditional-components-renderer";
import styles from "./AscDropdown.module.scss";
import { useRef } from "react";
import AscDropdownAndOptionsWrapper, {
  AscDropdownAndOptionsWrapperPropsAlignment,
} from "./AscDropdownAndOptionsWrapper/AscDropdownAndOptionsWrapper";
import {
  AscDropdownOption,
  AscDropdownOptionsContainer,
} from "./AscDropdownOptions/AscDropdownOptions";
import { ChevronDownIcon } from "../AscIcons";
import { useOutsideClick } from "@ascendnow/shared";

type AscDropdownVariants = "primary" | "secondary";

export interface AscDropdownProps {
  className?: string;
  optionContainerClassName?: string;
  optionItemClassName?: string;
  titleContainerClassName?: string;
  hideArrowIcon?: boolean;
  arrowIcon?: React.ReactNode;
  children?: JSX.Element[];
  title: React.ReactNode;
  variant?: AscDropdownVariants;
  header?: React.ReactNode;
  fullWidth?: boolean;
  alignment?: AscDropdownAndOptionsWrapperPropsAlignment;
  portalContentClassName?: string;
  afterSelect?: () => void;
  disabled?: boolean;
}

function AscDropdown({
  className,
  optionContainerClassName,
  optionItemClassName,
  titleContainerClassName,
  hideArrowIcon = false,
  arrowIcon,
  children,
  title,
  variant = "primary",
  header,
  fullWidth,
  alignment,
  portalContentClassName,
  disabled = false,
  afterSelect,
}: AscDropdownProps) {
  const toggleIconContainerRef = useRef<HTMLDivElement>(null);
  const {
    ref,
    isVisible: isOpen,
    setIsVisible: setIsOpen,
  } = useOutsideClick<HTMLDivElement>(false);

  const open = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (disabled) {
      return;
    }
    e.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const close = () => {
    setIsOpen(false);
  };

  const handleAfterSelect = () => {
    close();
    afterSelect?.();
  };

  const suffixIcon = arrowIcon ? arrowIcon : <ChevronDownIcon />;

  return (
    <AscDropdownAndOptionsWrapper
      alignment={alignment}
      contentClassName={portalContentClassName}
      content={
        <AscDropdownOptionsContainer
          ref={ref}
          header={header}
          children={children}
          className={optionContainerClassName}
          optionItemClassName={optionItemClassName}
          afterSelect={handleAfterSelect}
        />
      }
      visible={isOpen}
      onClickOutsideContainer={close}
      toggleIconContainerRef={toggleIconContainerRef}
      fullWidth={fullWidth}
      className={classNames(
        styles.ground,
        {
          [styles.isOpen]: isOpen,
          [styles.primary]: variant === "primary",
          [styles.secondary]: variant === "secondary",
          [styles.fullWidth]: fullWidth,
          [styles.disabled]: disabled,
        },
        className
      )}
    >
      <div
        className={classNames(styles.innerContainer, titleContainerClassName)}
        onClick={open}
      >
        <div onClick={open} className={styles.title}>
          {title}
        </div>

        <If test={!hideArrowIcon}>
          <div ref={toggleIconContainerRef} onClick={open}>
            <span className={classNames(styles.suffixIcon, styles.openIcon)}>
              {suffixIcon}
            </span>
          </div>
        </If>
      </div>
    </AscDropdownAndOptionsWrapper>
  );
}

AscDropdown.Option = AscDropdownOption;

export default AscDropdown;
